import '../../../Common.scss';
import '../../Common.scss';
import './BillingHistory.scss';
import { ReactComponent as RestoreIcon } from 'images/icons/Restore.svg';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow } from '@trackman/web-shared-components';
import { useState } from 'react';
import { InvoiceData } from 'types/invoices';
import { formatDate } from 'utilities/formatDate';
import { useServices, useStores } from 'index';

export const BillingHistory = ({ billingHistory }: { billingHistory: InvoiceData[] }) => {
  const { t } = useTranslation();
  const service = useServices().invoicesService;
  const notificationsStore = useStores().notificationStore;
  const [page, setPage] = useState(0);
  const [entriesPerPage, setEntriesPerPage] = useState(5);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const entries = billingHistory.map((entry) => ({
    id: entry.uuid,
    name: entry.line_items[0].description,
    invoiceNumber: entry.transactions[0] ? Number(entry.transactions[0].invoice.number) : null,
    paymentDate: formatDate(entry.created_at),
    discount: entry.discount === 0 ? '-' : `$${entry.discount}`,
    price: `$${entry.total}`,
    endDate: formatDate(entry.line_items[0].end_date),
  }));

  const pageTableEntries = entries.slice(page * entriesPerPage, page * entriesPerPage + entriesPerPage);

  const handleChangeRowsPerPage = (event) => {
    setEntriesPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const downloadInvoice = async (invoiceNumber: number) => {
    setIsDownloading(true);
    notificationsStore.addToast(
      t('PreparingDownload', { ns: 'certificate' }),
      t('YourInvoiceDownloadIsBeingPrepared', { ns: 'billingInformation' }),
      'info'
    );

    try {
      await service.download(invoiceNumber);
      notificationsStore.addToast(
        t('DownloadReady', { ns: 'certificate' }),
        t('YourInvoiceDownloadIsReady', { ns: 'billingInformation' }),
        'success'
      );
    } catch {
      notificationsStore.addToast(
        t('DownloadFailed', { ns: 'certificate' }),
        t('InvoiceDownloadError', { ns: 'billingInformation' }),
        'error'
      );
    }
    setIsDownloading(false);
  };

  return (
    <>
      {billingHistory.length ? (
        <div className='profile-section billing-history-section'>
          <div className='profile-section-title'>
            <RestoreIcon />
            {t('BillingHistory', { ns: 'billingInformation' })}
          </div>
          <Table sx={{ minWidth: 650 }} aria-label='simple table' className='tmu-table'>
            <TableHead>
              <TableRow>
                <TableCell>{t('PlanName', { ns: 'billingInformation' })}</TableCell>
                <TableCell align='right'>{t('PaymentDate', { ns: 'billingInformation' })}</TableCell>
                <TableCell align='right'>{t('Discount', { ns: 'billingInformation' })}</TableCell>
                <TableCell align='right'>{t('Price', { ns: 'billingInformation' })}</TableCell>
                <TableCell align='right'>{t('Expiry', { ns: 'billingInformation' })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageTableEntries.map((row) => (
                <TableRow key={row.id} className='billing-history-entry'>
                  <TableCell>
                    <span className='plan-name'>{row.name.toLowerCase()} </span>
                    {row.invoiceNumber && (
                      <span
                        className='link download-invoice'
                        onClick={() => row.invoiceNumber !== null && downloadInvoice(row.invoiceNumber)}
                      >
                        {t('Receipt', { ns: 'billingInformation' })}
                      </span>
                    )}
                  </TableCell>
                  <TableCell align='right'>{row.paymentDate}</TableCell>
                  <TableCell align='right'>{row.discount}</TableCell>
                  <TableCell align='right'>{row.price}</TableCell>
                  <TableCell align='right'>{row.endDate}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                  colSpan={5}
                  count={entries.length}
                  page={page}
                  rowsPerPage={entriesPerPage}
                  labelRowsPerPage={t('RowsPerPage', { ns: 'general' })}
                  onPageChange={(e, newPage: number) => setPage(newPage)}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </div>
      ) : null}
    </>
  );
};
